import * as React from 'react';
import { useState } from 'react';
import { EditorProps } from '../types/editor-props';
import {
  EditVatSummaryModal,
  type VatSummaryFormData
} from '../../../../modules/modals/EditVatSummaryModal';
import {
  useInvoiceCurrencyCode,
  useInvoiceRecomputeVatSummary,
  useInvoiceTotalAmountInclVat,
  useInvoiceTotalAmountNoVat,
  useInvoiceVatRateOptions,
  useVatSummaryCurrencyCode,
  useVatSummaryExchangeRate
} from '../../../../providers/InvoiceProvider';
import { VatSummaryFormatter } from '../formatters/VatSummaryFormatter';
import { computeVatSummaryTotals, toVatSummaryItems } from '../utils/vat-summary';

/**
 * Editor for structured VAT summary values.
 * Renders the current totals/breakdown inline and opens an EditVatSummaryModal.
 * On save, awaits `onCommit` so a rejected save (e.g. via
 * `onSubmitUpdate({ rethrow: true })`) keeps the modal open and surfaces the
 * error inline.
 */
export function VatSummaryEditor({ value, onChange, onCommit, onCancel, errors }: EditorProps) {
  const [open, setOpen] = useState(true);
  const vatSummaryCurrencyCode = useVatSummaryCurrencyCode();
  const vatSummaryExchangeRate = useVatSummaryExchangeRate();
  const invoiceCurrencyCode = useInvoiceCurrencyCode();
  const vatRateOptions = useInvoiceVatRateOptions();
  const invoiceTotalAmountInclVat = useInvoiceTotalAmountInclVat();
  const invoiceTotalAmountNoVat = useInvoiceTotalAmountNoVat();
  // Host-supplied via InvoiceProvider; undefined means no line-derived hints.
  const onRecomputeVatSummary = useInvoiceRecomputeVatSummary();

  // Tolerant of either incoming shape: the host typically passes
  // `VatSummaryItem[]`, but EditableField re-renders the editor mid-save with
  // the full `VatSummaryFormData` object (because `handleCommit` does
  // `setValue(newVal)` synchronously, before awaiting onSave).
  const items = toVatSummaryItems(value);
  const rowTotals = computeVatSummaryTotals(items);
  // Prefer the invoice's persisted totals so the mismatch warning compares the
  // breakdown against the actual invoice total, and so saving doesn't silently
  // overwrite the invoice totals with row sums. Falls back to row-derived
  // totals when the invoice has none yet (bootstrap flow).
  const currentData = {
    totalAmount: invoiceTotalAmountInclVat ?? rowTotals.totalAmount,
    totalAmountNoVat: invoiceTotalAmountNoVat ?? rowTotals.totalAmountNoVat,
    vatSummary: rowTotals.vatSummary,
    vatSummaryCurrencyCode,
    vatSummaryExchangeRate
  };

  const handleSave = async (next: VatSummaryFormData) => {
    if (onCommit) {
      await onCommit(next);
    } else {
      onChange(next);
    }
  };

  const handleClose = () => {
    setOpen(false);
    onCancel?.();
  };

  return (
    <>
      <div className='w-full'>
        <VatSummaryFormatter value={items} errors={errors} />
      </div>
      <EditVatSummaryModal
        open={open}
        currentData={currentData}
        onClose={handleClose}
        onSave={handleSave}
        currencyCode={vatSummaryCurrencyCode ?? invoiceCurrencyCode}
        vatRateOptions={vatRateOptions.length > 0 ? [...vatRateOptions] : undefined}
        errors={errors}
        onRecomputeVatSummary={onRecomputeVatSummary}
      />
    </>
  );
}

(VatSummaryEditor as React.FC).displayName = 'VatSummaryEditor';
